import {
  CcfBox,
  CcfCard,
  CcfDivider,
  CcfPlusIcon,
  CcfRightArrowIcon,
  CcfSnackBar,
  CcfTypography,
  CcfUpArrowIcon,
  DividerOrientation,
  DividerVariant,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { activeContactSelector } from '../../../ccf-inbox/ccf-inbox.slice';
import { useSelector } from 'react-redux';
import { Button, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import styles from './ccf-consumer-card-overview-tab.module.scss';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { getAppSpaceRatio } from '../../../global.app.slice';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { selectLoginCredentialsState } from '../../../login/+state/login.slice';
import { notesHistoryListData } from '../ccf-consumer-card-notes-tab/mock-data/notes-history-mock-data';

/* eslint-disable-next-line */
export interface CcfConsumerCardOverviewTabProps {}

/**
 * CcfConsumerCardOverviewTab - returns app space panel
 * @param props - CcfConsumerCardOverviewTabProps
 * @returns - appspace
 * @example - `<CcfConsumerCardOverviewTab />`
 */
export function CcfConsumerCardOverviewTab(
  props: CcfConsumerCardOverviewTabProps
) {
  const activeContact = useSelector(activeContactSelector);
  const [overviewMinValue, setOverviewMinValue] = useState(false);
  const [notesData, setNotesData]: any = useState([]);

  const appSpaceRatio = useSelector(getAppSpaceRatio);
  const [translate] = useTranslator();
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const loginCredentials = useSelector(selectLoginCredentialsState);
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');
  const name = isAuthenticated
    ? `${
        loginCredentialsArray[1].charAt(0).toUpperCase() +
        loginCredentialsArray[1].slice(1).toLowerCase()
      }`
    : loginCredentials?.lastName;

  useEffect(() => {
    if (activeContact?.notes) {
      const formattedNotes = Array.isArray(activeContact.notes)
        ? activeContact.notes
            .slice(0, 3)
            .map((x: any, i: any) =>
              i == 0
                ? {
                    ...x,
                    expandedMaxContent: true,
                    agentName:
                      notesHistoryListData[0].agentName === x.agentName
                        ? `Agent ${name}`
                        : x.agentName,
                  }
                : { ...x }
            )
        : [];

      setNotesData(formattedNotes);
    }
  }, [activeContact?.notes]);

  useEffect(() => {
    if (appSpaceRatio && appSpaceRatio <= 40) {
      setOverviewMinValue(true);
    } else {
      setOverviewMinValue(false);
    }
  }, [appSpaceRatio]);

  const [viewDetails, setViewDetails] = useState(false);

  const consumerDetails = [
    {
      key: 'FULLNAME',
      value: `${[
        activeContact?.customer?.firstName,
        activeContact?.customer?.middleName,
        activeContact?.customer?.lastName,
      ]
        .filter(Boolean)
        .join(' ')}`,
    },
    { key: 'ACCOUNTLEVEL', value: activeContact?.customer?.accountLevel },
    { key: 'BALANCE', value: activeContact?.customer?.balance },
    { key: 'DATEOFBIRTH', value: activeContact?.customer?.dateOfBirth },
    { key: 'CUSTOMERSINCE', value: activeContact?.customer?.customerSince },
    { key: 'ACCOUNT', value: activeContact?.id },
    { key: 'NEXTPAYMENT', value: activeContact?.customer?.nextPayment },
    {
      key: 'PREFERREDCONTACTMETHOD',
      value: activeContact?.customer.phoneNo,
    },
    // { key: 'ADDRESS', value: activeContact?.customer.customerName },
  ];

  const consumerNotes = [
    {
      index: 0,
      expanded: true,
      title: notesData[0]?.noteTitle,
      minContent: `${dayjs(new Date(notesData[0]?.date)).format('D/M/YYYY')} ${
        notesData[0]?.time
      } | ${notesData[0]?.subTitle} \n#423-INCL-0246 | ${
        notesData[0]?.agentName
      }`,
      maxContent: `${notesData[0]?.noteContent}`,
    },
    {
      index: 1,
      expanded: false,
      title: notesData[1]?.noteTitle,
      minContent: `${dayjs(new Date(notesData[1]?.date)).format('D/M/YYYY')} ${
        notesData[1]?.time
      } | ${notesData[1]?.subTitle} \n#423-INCL-0246 | ${
        notesData[1]?.agentName
      }`,
      maxContent: `${notesData[1]?.noteContent}`,
    },
    {
      index: 2,
      expanded: false,
      title: notesData[2]?.noteTitle,
      minContent: `${dayjs(new Date(notesData[2]?.date)).format('D/M/YYYY')} ${
        notesData[2]?.time
      } | ${notesData[2]?.subTitle} \n#423-INCL-0246 | ${
        notesData[2]?.agentName
      }`,
      maxContent: `${notesData [2]?.noteContent}`,
    },
  ];

  useEffect(() => {
    setNotes(consumerNotes);
  }, [notesData]);
  const latestSummary = [
    {
      index: 0,
      expanded: false,
       minContent: activeContact?.media==='Digital' ||
                   activeContact?.media==='Voice' ||
                   activeContact?.media==='Voicemail'?
                   `${activeContact?.perviousInteractionJsonData?.journeySummaryText.split(' ').map((word: string, index: number) => index === 0 ? activeContact?.customer?.firstName : word).join(' ')}`: 
                   `${activeContact?.customer?.firstName+ ' ' + activeContact?.customer?.lastName}`
    },
  ];
  const [notes, setNotes] = useState(consumerNotes);

  /**
   * Handles the click event of the right arrow button.
   * @param index - The index of the note.
   * @example
   * @param flag - The flag indicating whether the note should be expanded or not.
   */
  const handleRightArrowClick = (index: any, flag: any) => {
    const updatedData = notes.map((x: any) =>
      x.index === index ? { ...x, expanded: flag } : { ...x, expanded: false }
    );
    setNotes(updatedData);
  };

  return (
    <>
      <CcfBox
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          overflowY: 'auto',
        }}
        sx={
          overviewMinValue
            ? { flexDirection: 'column', overflowY: 'auto' }
            : { flexDirection: 'row' }
        }
        id="agent_appSpace_overviewTab_parentContainer"
      >
        <div
          className={styles.detailContainer}
          style={overviewMinValue ? { width: '100%' } : { width: '50%' }}
        >
          <div className={styles.basicInfo}>
            <div style={{ columnCount: 2 }}>
              {consumerDetails.map((x: any) => (
                <div
                  className={styles.keyValueContainer}
                  id="agent_appSpace_overviewTab_consumerDetails"
                >
                  <CcfTypography
                    id={`agent_appSpace_overviewTab_consumerDetails_${x.key}`}
                    className={styles.keyLabel}
                  >
                    {translate(x.key)}
                  </CcfTypography>
                  <CcfTypography
                    id={`agent_appSpace_overviewTab_consumerDetails_${x.value}`}
                    className={styles.valueLabel}
                  >
                    {x.value}{' '}
                  </CcfTypography>
                </div>
              ))}
            </div>{' '}
            <div className={styles.keyValueContainer}>
              <CcfTypography
                id={`agent_appSpace_overviewTab_consumerDetails_address`}
                className={styles.keyLabel}
              >
                {translate('ADDRESS')}
              </CcfTypography>
              <CcfTypography
                id={`agent_appSpace_overviewTab_consumerDetails_addressValue`}
                className={styles.valueLabel}
              >
                {activeContact?.customer?.customerPrimaryAddress?.address1.toString()}
                {activeContact?.customer?.customerPrimaryAddress?.address2.toString()}{' '}
                {<br />}
                {activeContact?.customer?.customerPrimaryAddress?.city.toString()}{' '}
                {activeContact?.customer?.customerPrimaryAddress?.state.toString()}
                {', '}
                {activeContact?.customer?.customerPrimaryAddress?.zip.toString()}{' '}
                {activeContact?.customer?.customerPrimaryAddress?.country.toString()}
              </CcfTypography>
            </div>
            <CcfDivider
              orientation={DividerOrientation.HORIZONTAL}
              variant={DividerVariant.FULLWIDTH}
              sx={{
                width: '100%',
                borderColor: '#D2D8DB',
                margin: '10px 0px',
              }}
            />
          </div>

          <div
            style={{ padding: '5px' }}
            id={`agent_appSpace_overviewTab_latestSummaryDiv`}
          >
            <CcfTypography className={styles.keyLabel}>
              {translate('LATESTINTERACTIONSUMMARY')}
            </CcfTypography>
            {latestSummary.map((summary: any) => (
              <CcfTypography
                id={`agent_appSpace_overviewTab_latestMinSummary`}
                className={styles.valueLabel}
              >
                {summary?.minContent}
              </CcfTypography>
            ))}
            <div>
              <button
                id={`agent_appSpace_overviewTab_latestSummary_ViewDetails`}
                className={styles.viewButton}
                onClick={() => setViewDetails(!viewDetails)}
              >
                <CcfTypography
                  translationKey={
                    viewDetails
                      ? translate('hideAllDetails')
                      : translate('viewAllDetails')
                  }
                />
                {viewDetails ? (
                  <CcfUpArrowIcon className={styles.expandedSummaryIcon} />
                ) : (
                  <CcfRightArrowIcon
                    className="pointerEvent"
                    style={{ fontSize: '1rem', marginTop: '4px' }}
                  />
                )}
              </button>
            </div>
            <CcfDivider
              orientation={DividerOrientation.HORIZONTAL}
              variant={DividerVariant.FULLWIDTH}
              sx={{
                width: '100%',
                borderColor: '#D2D8DB',
                marginTop: '5px',
              }}
            />
          </div>
        </div>
        <div
          style={
            overviewMinValue
              ? { width: '100%', padding: '10px' }
              : { width: '50%', padding: '10px' }
          }
          id={`agent_appSpace_overviewTab_latestNotes_parentDiv`}
        >
          <div className={styles.noteParentContainer}>
            <CcfTypography className={styles.keyLabel}>
              {translate('LATESTNOTES')}
            </CcfTypography>
            <div style={{ display: 'flex', gap: '5px' }}>
              <CcfBox id={`consumer_overviewTab_addButton`}>
                <Button className={styles.buttonParent}>
                  <AddIcon
                    style={{ color: '#003D7A', height: '14px', width: '14px' }}
                  />
                  <CcfTypography
                    className={styles.buttonStyle}
                    translationKey={'add'}
                  ></CcfTypography>
                </Button>
              </CcfBox>
              <CcfBox id={`consumer_overviewTab_viewAllButton`}>
                <Button className={styles.buttonParent}>
                  <AddIcon
                    style={{ color: '#003D7A', height: '14px', width: '14px' }}
                  />
                  <CcfTypography className={styles.buttonStyle}>
                    {!overviewMinValue
                      ? translate('view')
                      : translate('viewAll')}
                  </CcfTypography>
                </Button>
              </CcfBox>
            </div>
          </div>
          <div
            className={styles.eachNoteContainer}
            id={`agent_appSpace_overviewTab_notesParentDiv`}
          >
            {notes.map((note: any) => (
              <CcfCard
                id={`ccf-consumer-overview-notes-${note.title}`}
                sx={{
                  borderBottom: 'none',
                  border: '1px solid #D2D8DB',
                  padding: '10px 0px',
                  marginBottom: '5px',
                }}
                className={`${
                  note.expanded ? styles.activeNote : 'inactiveNote'
                }`}
              >
                <CcfTypography
                  style={{ padding: '0px 10px' }}
                  className={styles.valueLabel}
                >
                  {note.title}
                </CcfTypography>

                <div className={styles.noteMinContent}>
                  <CcfTypography
                    id={`agent_appSpace_overviewTab_notesMinContent`}
                    style={{ whiteSpace: 'pre-wrap' }}
                    className={styles.keyLabel}
                  >
                    {note.minContent}
                  </CcfTypography>
                  {note.expanded ? (
                    <CcfUpArrowIcon
                      id={`overview-notes-${note.title}-upArrow`}
                      onClick={() => handleRightArrowClick(note.index, false)}
                      className={styles.expandedIcon}
                    />
                  ) : (
                    <CcfRightArrowIcon
                      id={`overview-notes-${note.title}-rightArrow`}
                      style={{ fontSize: '1.1rem' }}
                      className="pointerEvent"
                      onClick={() => handleRightArrowClick(note.index, true)}
                    />
                  )}
                </div>
                {note.expanded ? (
                  <div>
                    <CcfDivider
                      orientation={DividerOrientation.HORIZONTAL}
                      variant={DividerVariant.FULLWIDTH}
                      sx={{
                        width: '100%',
                        borderColor: '#D2D8DB',
                        margin: '10px 0px',
                      }}
                    />
                    <CcfTypography
                      id={`agent_appSpace_overviewTab_notesMaxContent`}
                      style={{
                        fontSize: '13px',
                        padding: '0px 10px',
                        lineHeight: '18.59px',
                      }}
                    >
                      {note.maxContent}
                    </CcfTypography>
                  </div>
                ) : null}
              </CcfCard>
            ))}
          </div>
        </div>
      </CcfBox>
      <Divider style={{ height: '1px', color: '#D9E1E2' }} />
    </>
  );
}

export default CcfConsumerCardOverviewTab;
